import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewProjectPage = () => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    client_name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    project_type: 'Other',
    start_date: '',
    end_date: '',
    total_budget: '',
    status: 'Active'
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);

    // Combine address fields
    const addressParts = [formData.street, formData.city, formData.state, formData.zip].filter(Boolean);
    const address = addressParts.join(', ');

    try {
      const response = await axios.post(`${API}/projects/`, {
        name: formData.name,
        client_name: formData.client_name,
        address: address || null,
        project_type: formData.project_type,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        total_budget: parseFloat(formData.total_budget) || 0,
        status: formData.status
      }, {
        withCredentials: true
      });
      
      toast.success('Project created!');
      navigate(`/projects/${response.data.project_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="new-project-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b-2 border-black p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
            className="h-12 w-12 flex items-center justify-center border border-zinc-200 bg-white hover:bg-zinc-50"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-heading font-bold uppercase tracking-wide">New Project</h1>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4 space-y-6 pb-24">
        {/* Basic Info Section */}
        <div className="bg-white border border-zinc-200 p-4 ">
          <h2 className="text-sm font-heading font-bold uppercase tracking-wide uppercase tracking-wide text-zinc-500 mb-4">Basic Information</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                Project Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                data-testid="project-name-input"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary"
                placeholder="Downtown Office HVAC Retrofit"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                Client Name *
              </label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                required
                data-testid="client-name-input"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary"
                placeholder="ABC Development Corp"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                Project Type
              </label>
              <select
                name="project_type"
                value={formData.project_type}
                onChange={handleChange}
                data-testid="project-type-select"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary"
              >
                <option value="Electrical">Electrical</option>
                <option value="Plumbing">Plumbing</option>
                <option value="HVAC">HVAC</option>
                <option value="Mechanical">Mechanical</option>
                <option value="Multi-Trade">Multi-Trade</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="bg-white border border-zinc-200 p-4 ">
          <h2 className="text-sm font-heading font-bold uppercase tracking-wide uppercase tracking-wide text-zinc-500 mb-4">Job Site Address</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                Street Address
              </label>
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleChange}
                data-testid="street-input"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary"
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  data-testid="city-input"
                  className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary"
                  placeholder="Los Angeles"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  data-testid="state-input"
                  className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary"
                  placeholder="CA"
                  maxLength={2}
                />
              </div>
            </div>

            <div className="w-1/2">
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                ZIP Code
              </label>
              <input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleChange}
                data-testid="zip-input"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary"
                placeholder="90001"
                maxLength={10}
              />
            </div>
          </div>
        </div>

        {/* Schedule Section */}
        <div className="bg-white border border-zinc-200 p-4 ">
          <h2 className="text-sm font-heading font-bold uppercase tracking-wide uppercase tracking-wide text-zinc-500 mb-4">Schedule</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                Start Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none" />
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  data-testid="start-date-input"
                  className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg pl-12 pr-4 focus:outline-none focus:border-primary"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                End Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none" />
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  data-testid="end-date-input"
                  className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg pl-12 pr-4 focus:outline-none focus:border-primary"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Budget Section */}
        <div className="bg-white border border-zinc-200 p-4 ">
          <h2 className="text-sm font-heading font-bold uppercase tracking-wide uppercase tracking-wide text-zinc-500 mb-4">Budget & Status</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                Total Materials Budget
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-zinc-500">$</span>
                <input
                  type="number"
                  name="total_budget"
                  value={formData.total_budget}
                  onChange={handleChange}
                  data-testid="budget-input"
                  className="w-full h-14 border border-zinc-200 bg-white text-zinc-900 text-lg pl-10 pr-4 focus:outline-none focus:border-primary"
                  placeholder="250,000"
                  min="0"
                  step="1000"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                data-testid="status-select"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 focus:outline-none focus:border-primary"
              >
                <option value="Active">Active</option>
                <option value="On Hold">On Hold</option>
                <option value="Complete">Complete</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submit Button - Fixed at bottom on mobile */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t-2 border-black md:relative md:border-0 md:p-0 md:bg-transparent">
          <button
            type="submit"
            disabled={creating}
            data-testid="create-project-btn"
            className="w-full h-14 bg-primary text-zinc-900 font-bold uppercase tracking-wide border border-zinc-200  hover:bg-primary-hover active:translate-y-1 active: transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? 'Creating Project...' : 'Create Project'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewProjectPage;
